// client/src/components/templates/anamnese/AnamnesePreview.js - VERSÃO COMPLETA COM IMPRESSÃO A4
import React, { useState, useRef, useEffect } from 'react';
import { FileText, Download, Printer, Eye, EyeOff, AlertCircle, Loader2, ArrowLeft, ImageIcon, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import useAlert from '../../../hooks/useAlert';

const PreviewItem = ({ item }) => {
    switch (item.type) {
        case 'Título': 
            return <h2 className="preview-title">{item.label}</h2>;
        case 'Texto Simples': 
            return <p className="preview-text">{item.label}</p>;
        case 'Separador': 
            return <hr className="preview-separator" />;
        case 'Resposta em Texto': 
            return (
                <div className="preview-field">
                    <label className="preview-field-label">{item.label}</label>
                    <textarea 
                        className="preview-input" 
                        readOnly 
                        placeholder="Campo para resposta em texto." 
                        rows="3"
                    />
                </div>
            );
        case 'Verdadeiro ou Falso': 
            return (
                <div className="preview-field">
                    <label className="preview-field-label">{item.label}</label>
                    <div className="preview-options">
                        <label className="preview-option-item">
                            <input type="radio" name={item.id} disabled /> 
                            Sim
                        </label>
                        <label className="preview-option-item">
                            <input type="radio" name={item.id} disabled /> 
                            Não
                        </label>
                    </div>
                </div>
            );
        case 'Opções': 
            return (
                <div className="preview-field">
                    <label className="preview-field-label">{item.label}</label>
                    <div className="preview-options">
                        {(item.options || []).map((opt, i) => (
                            <label key={i} className="preview-option-item">
                                <input type="radio" name={item.id} disabled /> 
                                {opt.label || opt}
                            </label>
                        ))}
                    </div>
                </div>
            );
        case 'Multi Opções': 
            return (
                <div className="preview-field">
                    <label className="preview-field-label">{item.label}</label>
                    <div className="preview-options">
                        {(item.options || []).map((opt, i) => (
                            <label key={i} className="preview-option-item">
                                <input type="checkbox" disabled /> 
                                {opt.label || opt}
                            </label>
                        ))}
                    </div>
                </div>
            );
        case 'Galeria': 
            return (
                <div className="preview-field">
                    <label className="preview-field-label">{item.label}</label>
                    <div className="preview-gallery-placeholder">
                        <ImageIcon size={24} />
                        <p>Área para upload de imagens.</p>
                    </div>
                </div>
            );
        default: 
            return <p className="preview-unknown">Tipo de campo não reconhecido: {item.type}</p>;
    }
};

const AnamnesePreview = ({ template, onClose }) => {
    const { setAlert } = useAlert();
    const [showPreview, setShowPreview] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageContents, setPageContents] = useState([]);
    const iframeRef = useRef(null);

    // Função para dividir conteúdo em páginas para anamnese
    const splitContentIntoPages = (content) => {
        if (!content || !Array.isArray(content)) return [''];
        
        const items = content;
        const pages = [];
        let currentPageItems = [];
        let currentPageHeight = 0;
        
        // Altura máxima aproximada por página (em "unidades")
        const maxPageHeight = 40;
        
        items.forEach((item, index) => {
            let itemHeight = 1;
            
            // Definir altura estimada para cada tipo de item
            switch (item.type) {
                case 'Título':
                    itemHeight = 2;
                    break;
                case 'Texto Simples':
                    itemHeight = 1.5;
                    break;
                case 'Separador':
                    itemHeight = 0.5;
                    break;
                case 'Resposta em Texto':
                    itemHeight = 4;
                    break;
                case 'Verdadeiro ou Falso':
                    itemHeight = 2;
                    break;
                case 'Opções':
                    itemHeight = Math.max(2, (item.options?.length || 0) * 0.5);
                    break;
                case 'Multi Opções':
                    itemHeight = Math.max(2, (item.options?.length || 0) * 0.5);
                    break;
                case 'Galeria':
                    itemHeight = 3;
                    break;
                default:
                    itemHeight = 1;
            }
            
            // Se adicionar este item exceder a altura máxima, criar nova página
            if (currentPageHeight + itemHeight > maxPageHeight && currentPageItems.length > 0) {
                pages.push([...currentPageItems]);
                currentPageItems = [item];
                currentPageHeight = itemHeight;
            } else {
                currentPageItems.push(item);
                currentPageHeight += itemHeight;
            }
            
            // Último item
            if (index === items.length - 1 && currentPageItems.length > 0) {
                pages.push([...currentPageItems]);
            }
        });
        
        console.log(`📄 [DEBUG] Anamnese dividida em ${pages.length} páginas`);
        return pages;
    };

    // Função para criar conteúdo HTML de uma página
    const createPageContent = (pageNumber) => {
        if (!template?.conteudo || pageContents.length === 0) {
            return createErrorContent();
        }

        const pageItems = pageContents[pageNumber - 1] || [];
        
        const itemsHTML = pageItems.map((item, index) => {
            switch (item.type) {
                case 'Título': 
                    return `<h2 class="anamnese-title">${item.label}</h2>`;
                case 'Texto Simples': 
                    return `<p class="anamnese-text">${item.label}</p>`;
                case 'Separador': 
                    return `<hr class="anamnese-separator" />`;
                case 'Resposta em Texto': 
                    return `
                        <div class="anamnese-field">
                            <label class="anamnese-field-label">${item.label}</label>
                            <textarea class="anamnese-input" readonly placeholder="Resposta..." rows="3"></textarea>
                        </div>
                    `;
                case 'Verdadeiro ou Falso': 
                    return `
                        <div class="anamnese-field">
                            <label class="anamnese-field-label">${item.label}</label>
                            <div class="anamnese-options">
                                <label class="anamnese-option-item"><input type="radio" name="${item.id}" disabled /> Sim</label>
                                <label class="anamnese-option-item"><input type="radio" name="${item.id}" disabled /> Não</label>
                            </div>
                        </div>
                    `;
                case 'Opções': 
                    const optionsHTML = (item.options || []).map(opt => 
                        `<label class="anamnese-option-item"><input type="radio" name="${item.id}" disabled /> ${opt.label || opt}</label>`
                    ).join('');
                    return `
                        <div class="anamnese-field">
                            <label class="anamnese-field-label">${item.label}</label>
                            <div class="anamnese-options">${optionsHTML}</div>
                        </div>
                    `;
                case 'Multi Opções': 
                    const multiOptionsHTML = (item.options || []).map(opt => 
                        `<label class="anamnese-option-item"><input type="checkbox" disabled /> ${opt.label || opt}</label>`
                    ).join('');
                    return `
                        <div class="anamnese-field">
                            <label class="anamnese-field-label">${item.label}</label>
                            <div class="anamnese-options">${multiOptionsHTML}</div>
                        </div>
                    `;
                case 'Galeria': 
                    return `
                        <div class="anamnese-field">
                            <label class="anamnese-field-label">${item.label}</label>
                            <div class="anamnese-gallery">
                                <div class="gallery-placeholder">[Área para upload de imagens]</div>
                            </div>
                        </div>
                    `;
                default: 
                    return `<p class="anamnese-unknown">Tipo de campo não reconhecido: ${item.type}</p>`;
            }
        }).join('');

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Anamnese: ${template.nome} - Página ${pageNumber}</title>
                <style>
                    @page {
                        size: A4 portrait;
                        margin: 20mm;
                    }
                    
                    body {
                        font-family: Arial, Helvetica, sans-serif;
                        color: #222;
                        line-height: 1.4;
                        margin: 0;
                        padding: 0;
                        background: white;
                        width: 210mm;
                        min-height: 297mm;
                        box-sizing: border-box;
                    }
                    
                    .anamnese-container {
                        padding: 15mm;
                        min-height: 257mm;
                        background: white;
                        position: relative;
                    }
                    
                    .anamnese-header {
                        border-bottom: 2px solid #0b63d6;
                        padding-bottom: 10px;
                        margin-bottom: 20px;
                    }
                    
                    .anamnese-title-main {
                        font-size: 24px;
                        color: #0b63d6;
                        margin: 0 0 10px 0;
                        text-align: center;
                    }
                    
                    .anamnese-description {
                        font-size: 14px;
                        color: #666;
                        text-align: center;
                        margin: 0;
                    }
                    
                    .anamnese-content {
                        margin-top: 20px;
                    }
                    
                    .anamnese-title {
                        font-size: 18px;
                        color: #0b63d6;
                        margin: 20px 0 10px 0;
                        padding-bottom: 5px;
                        border-bottom: 1px solid #e6e9ee;
                    }
                    
                    .anamnese-text {
                        font-size: 14px;
                        color: #444;
                        line-height: 1.5;
                        margin: 10px 0;
                    }
                    
                    .anamnese-separator {
                        border: none;
                        border-top: 2px dashed #d1d5db;
                        margin: 20px 0;
                    }
                    
                    .anamnese-field {
                        margin: 15px 0;
                        padding: 15px;
                        background: #f8fafc;
                        border-left: 4px solid #0b63d6;
                        border-radius: 4px;
                    }
                    
                    .anamnese-field-label {
                        display: block;
                        font-weight: 600;
                        color: #374151;
                        margin-bottom: 10px;
                        font-size: 14px;
                    }
                    
                    .anamnese-input {
                        width: 100%;
                        padding: 8px 12px;
                        border: 1px solid #d1d5db;
                        border-radius: 4px;
                        background: white;
                        font-size: 14px;
                        resize: vertical;
                        min-height: 80px;
                    }
                    
                    .anamnese-options {
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                    }
                    
                    .anamnese-option-item {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        padding: 8px;
                        background: white;
                        border: 1px solid #e5e7eb;
                        border-radius: 4px;
                        cursor: not-allowed;
                    }
                    
                    .anamnese-gallery {
                        padding: 20px;
                        background: #f1f5f9;
                        border: 2px dashed #cbd5e1;
                        border-radius: 4px;
                        text-align: center;
                        color: #64748b;
                    }
                    
                    .gallery-placeholder {
                        font-style: italic;
                    }
                    
                    .anamnese-unknown {
                        color: #ef4444;
                        font-style: italic;
                        padding: 10px;
                        background: #fef2f2;
                        border-radius: 4px;
                    }
                    
                    .anamnese-signature {
                        margin-top: 40px;
                        padding-top: 20px;
                        border-top: 2px solid #e5e7eb;
                        text-align: center;
                    }
                    
                    .signature-line {
                        width: 300px;
                        height: 1px;
                        background: #374151;
                        margin: 0 auto 10px auto;
                    }
                    
                    .signature-label {
                        color: #6b7280;
                        font-size: 12px;
                    }
                    
                    .page-number {
                        position: absolute;
                        bottom: 10mm;
                        right: 15mm;
                        font-size: 12px;
                        color: #666;
                    }
                </style>
            </head>
            <body>
                <div class="anamnese-container">
                    <div class="anamnese-header">
                        <h1 class="anamnese-title-main">${template.nome}</h1>
                        ${template.descricao ? `<p class="anamnese-description">${template.descricao}</p>` : ''}
                    </div>
                    
                    <div class="anamnese-content">
                        ${itemsHTML}
                    </div>
                    
                    ${pageNumber === totalPages && template.requerAssinatura ? `
                        <div class="anamnese-signature">
                            <div class="signature-line"></div>
                            <p class="signature-label">Assinatura do Cliente</p>
                        </div>
                    ` : ''}
                    
                    <div class="page-number">Página ${pageNumber} de ${totalPages}</div>
                </div>
            </body>
            </html>
        `;
    };

    // Função para criar conteúdo completo para impressão/PDF
    const createPrintContent = () => {
        if (!template?.conteudo || pageContents.length === 0) {
            return createErrorContent();
        }

        const pagesHTML = pageContents.map((pageItems, index) => {
            const itemsHTML = pageItems.map((item, itemIndex) => {
                switch (item.type) {
                    case 'Título': 
                        return `<h2 class="anamnese-title">${item.label}</h2>`;
                    case 'Texto Simples': 
                        return `<p class="anamnese-text">${item.label}</p>`;
                    case 'Separador': 
                        return `<hr class="anamnese-separator" />`;
                    case 'Resposta em Texto': 
                        return `
                            <div class="anamnese-field">
                                <label class="anamnese-field-label">${item.label}</label>
                                <textarea class="anamnese-input" readonly placeholder="Resposta..." rows="3"></textarea>
                            </div>
                        `;
                    case 'Verdadeiro ou Falso': 
                        return `
                            <div class="anamnese-field">
                                <label class="anamnese-field-label">${item.label}</label>
                                <div class="anamnese-options">
                                    <label class="anamnese-option-item"><input type="radio" name="${item.id}" disabled /> Sim</label>
                                    <label class="anamnese-option-item"><input type="radio" name="${item.id}" disabled /> Não</label>
                                </div>
                            </div>
                        `;
                    case 'Opções': 
                        const optionsHTML = (item.options || []).map(opt => 
                            `<label class="anamnese-option-item"><input type="radio" name="${item.id}" disabled /> ${opt.label || opt}</label>`
                        ).join('');
                        return `
                            <div class="anamnese-field">
                                <label class="anamnese-field-label">${item.label}</label>
                                <div class="anamnese-options">${optionsHTML}</div>
                            </div>
                        `;
                    case 'Multi Opções': 
                        const multiOptionsHTML = (item.options || []).map(opt => 
                            `<label class="anamnese-option-item"><input type="checkbox" disabled /> ${opt.label || opt}</label>`
                        ).join('');
                        return `
                            <div class="anamnese-field">
                                <label class="anamnese-field-label">${item.label}</label>
                                <div class="anamnese-options">${multiOptionsHTML}</div>
                            </div>
                        `;
                    case 'Galeria': 
                        return `
                            <div class="anamnese-field">
                                <label class="anamnese-field-label">${item.label}</label>
                                <div class="anamnese-gallery">
                                    <div class="gallery-placeholder">[Área para upload de imagens]</div>
                                </div>
                            </div>
                        `;
                    default: 
                        return `<p class="anamnese-unknown">Tipo de campo não reconhecido: ${item.type}</p>`;
                }
            }).join('');

            return `
                <div class="a4-page" style="page-break-after: ${index < pageContents.length - 1 ? 'always' : 'auto'};">
                    <div class="anamnese-container">
                        <div class="anamnese-header">
                            <h1 class="anamnese-title-main">${template.nome}</h1>
                            ${template.descricao ? `<p class="anamnese-description">${template.descricao}</p>` : ''}
                        </div>
                        
                        <div class="anamnese-content">
                            ${itemsHTML}
                        </div>
                        
                        ${index === pageContents.length - 1 && template.requerAssinatura ? `
                            <div class="anamnese-signature">
                                <div class="signature-line"></div>
                                <p class="signature-label">Assinatura do Cliente</p>
                            </div>
                        ` : ''}
                        
                        <div class="page-number">Página ${index + 1} de ${pageContents.length}</div>
                    </div>
                </div>
            `;
        }).join('');

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Anamnese: ${template.nome}</title>
                <style>
                    @page {
                        size: A4 portrait;
                        margin: 20mm;
                    }
                    
                    body {
                        font-family: Arial, Helvetica, sans-serif;
                        color: #222;
                        line-height: 1.4;
                        margin: 0;
                        padding: 0;
                        background: white;
                    }
                    
                    .a4-page {
                        width: 210mm;
                        min-height: 297mm;
                        padding: 0;
                        background: white;
                        box-sizing: border-box;
                        position: relative;
                    }
                    
                    .anamnese-container {
                        padding: 15mm;
                        min-height: 257mm;
                        background: white;
                        position: relative;
                    }
                    
                    .anamnese-header {
                        border-bottom: 2px solid #0b63d6;
                        padding-bottom: 10px;
                        margin-bottom: 20px;
                    }
                    
                    .anamnese-title-main {
                        font-size: 24px;
                        color: #0b63d6;
                        margin: 0 0 10px 0;
                        text-align: center;
                    }
                    
                    .anamnese-description {
                        font-size: 14px;
                        color: #666;
                        text-align: center;
                        margin: 0;
                    }
                    
                    .anamnese-content {
                        margin-top: 20px;
                    }
                    
                    .anamnese-title {
                        font-size: 18px;
                        color: #0b63d6;
                        margin: 20px 0 10px 0;
                        padding-bottom: 5px;
                        border-bottom: 1px solid #e6e9ee;
                    }
                    
                    .anamnese-text {
                        font-size: 14px;
                        color: #444;
                        line-height: 1.5;
                        margin: 10px 0;
                    }
                    
                    .anamnese-separator {
                        border: none;
                        border-top: 2px dashed #d1d5db;
                        margin: 20px 0;
                    }
                    
                    .anamnese-field {
                        margin: 15px 0;
                        padding: 15px;
                        background: #f8fafc;
                        border-left: 4px solid #0b63d6;
                        border-radius: 4px;
                    }
                    
                    .anamnese-field-label {
                        display: block;
                        font-weight: 600;
                        color: #374151;
                        margin-bottom: 10px;
                        font-size: 14px;
                    }
                    
                    .anamnese-input {
                        width: 100%;
                        padding: 8px 12px;
                        border: 1px solid #d1d5db;
                        border-radius: 4px;
                        background: white;
                        font-size: 14px;
                        resize: vertical;
                        min-height: 80px;
                    }
                    
                    .anamnese-options {
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                    }
                    
                    .anamnese-option-item {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        padding: 8px;
                        background: white;
                        border: 1px solid #e5e7eb;
                        border-radius: 4px;
                        cursor: not-allowed;
                    }
                    
                    .anamnese-gallery {
                        padding: 20px;
                        background: #f1f5f9;
                        border: 2px dashed #cbd5e1;
                        border-radius: 4px;
                        text-align: center;
                        color: #64748b;
                    }
                    
                    .gallery-placeholder {
                        font-style: italic;
                    }
                    
                    .anamnese-unknown {
                        color: #ef4444;
                        font-style: italic;
                        padding: 10px;
                        background: #fef2f2;
                        border-radius: 4px;
                    }
                    
                    .anamnese-signature {
                        margin-top: 40px;
                        padding-top: 20px;
                        border-top: 2px solid #e5e7eb;
                        text-align: center;
                    }
                    
                    .signature-line {
                        width: 300px;
                        height: 1px;
                        background: #374151;
                        margin: 0 auto 10px auto;
                    }
                    
                    .signature-label {
                        color: #6b7280;
                        font-size: 12px;
                    }
                    
                    .page-number {
                        position: absolute;
                        bottom: 10mm;
                        right: 15mm;
                        font-size: 12px;
                        color: #666;
                    }
                    
                    /* Garantir quebra de página na impressão */
                    @media print {
                        body {
                            margin: 0;
                            padding: 0;
                            background: white;
                        }
                        
                        .a4-page {
                            width: 210mm;
                            height: 297mm;
                            margin: 0;
                            padding: 0;
                            page-break-after: always;
                        }
                        
                        .a4-page:last-child {
                            page-break-after: auto;
                        }
                    }
                </style>
            </head>
            <body class="modo-a4">
                ${pagesHTML}
            </body>
            </html>
        `;
    };

    // Função para criar conteúdo de erro
    const createErrorContent = () => {
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Anamnese: ${template?.nome || 'N/A'}</title>
                <style>
                    @page { 
                        size: A4 portrait; 
                        margin: 20mm; 
                    }
                    body { 
                        font-family: Arial, sans-serif; 
                        line-height: 1.6; 
                        margin: 0; 
                        padding: 40px;
                        color: #333;
                        background: #f9f9f9;
                    }
                    .error-container { 
                        background: white;
                        border: 1px solid #e0e0e0;
                        border-radius: 8px;
                        padding: 40px;
                        margin: 50px auto;
                        max-width: 600px;
                        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    }
                    .error-title { 
                        color: #d32f2f; 
                        font-size: 24px; 
                        margin-bottom: 20px;
                        text-align: center;
                    }
                    .error-message {
                        text-align: center;
                        margin-bottom: 30px;
                        color: #666;
                    }
                    .support-message {
                        text-align: center;
                        margin-top: 30px;
                        color: #888;
                        font-style: italic;
                    }
                </style>
            </head>
            <body>
                <div class="error-container">
                    <h1 class="error-title">Anamnese Sem Conteúdo</h1>
                    <p class="error-message">Esta anamnese não possui conteúdo para visualização.</p>
                    <p class="support-message">Adicione campos à anamnese para visualizar a prévia.</p>
                </div>
            </body>
            </html>
        `;
    };

    // Funções de navegação de página
    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Funções de ação
    const handlePrint = () => {
        setIsLoading(true);
        const printContent = createPrintContent();
        const printWindow = window.open('', '_blank');
        
        if (!printWindow) {
            setAlert('Não foi possível abrir a janela de impressão. Verifique se o pop-up está bloqueado.', 'warning');
            setIsLoading(false);
            return;
        }
        
        printWindow.document.write(printContent);
        printWindow.document.close();
        
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            setIsLoading(false);
        }, 500);
    };

    const handleDownload = () => {
        setIsLoading(true);
        const content = createPrintContent();
        const blob = new Blob([content], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `anamnese-${template.nome}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setIsLoading(false);
    };

    const togglePreview = () => {
        setShowPreview(!showPreview);
    };

    // Processar conteúdo do template e dividir em páginas
    useEffect(() => {
        if (template?.conteudo && Array.isArray(template.conteudo)) {
            const pages = splitContentIntoPages(template.conteudo);
            setPageContents(pages);
            setTotalPages(pages.length);
            setCurrentPage(1);
            console.log(`📄 [DEBUG] Anamnese processada: ${pages.length} páginas criadas`);
        } else {
            setPageContents([]);
            setTotalPages(1);
            setCurrentPage(1);
        }
    }, [template]);

    if (!template) {
        return (
            <div className="content-panel">
                <div className="panel-state-wrapper">
                    <FileText size={48} className="text-gray-400" />
                    <p>Nenhuma anamnese selecionada para visualização</p>
                </div>
            </div>
        );
    }

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    const getTotalFields = () => {
        if (!template.conteudo || !Array.isArray(template.conteudo)) return 0;
        return template.conteudo.filter(item => 
            item.type !== 'Título' && item.type !== 'Texto Simples' && item.type !== 'Separador'
        ).length;
    };

    return (
        <div className="content-panel">
            {/* CABEÇALHO NO ESTILO DO FORMULÁRIO */}
            <div className="form-header">
                <h3 className="form-title">Visualizando: {template.nome}</h3>
                <button 
                    type="button" 
                    onClick={onClose} 
                    title="Fechar" 
                    className="form-close-button"
                >
                    <XCircle size={24} />
                </button>
            </div>

            {/* RESUMO DA ANAMNESE */}
            <fieldset className="form-section">
                <legend className="form-section-title">Resumo da Anamnese</legend>
                <div className="form-grid-container-auto-fit">
                    <div className="form-group">
                        <label className="form-label">Nome:</label>
                        <div className="form-input form-input-disabled">{template.nome}</div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Status:</label>
                        <div className="form-input form-input-disabled">
                            <span className={`badge ${template.ativo ? 'badge-green' : 'badge-gray'}`}>
                                {template.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Campos:</label>
                        <div className="form-input form-input-disabled highlight">
                            {getTotalFields()} campos
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Páginas:</label>
                        <div className="form-input form-input-disabled highlight">
                            {totalPages}
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Requer Assinatura:</label>
                        <div className="form-input form-input-disabled">
                            <span className={`badge ${template.requerAssinatura ? 'badge-blue' : 'badge-gray'}`}>
                                {template.requerAssinatura ? 'Sim' : 'Não'}
                            </span>
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Modificado em:</label>
                        <div className="form-input form-input-disabled">
                            {formatDate(template.updatedAt)}
                        </div>
                    </div>
                </div>
            </fieldset>

            {/* CONTROLES DE VISUALIZAÇÃO */}
            <fieldset className="form-section">
                <legend className="form-section-title">Controles de Visualização</legend>
                <div className="editor-toolbar">
                    <button 
                        onClick={togglePreview}
                        className="action-button action-button-secondary"
                        disabled={isLoading}
                    >
                        {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
                        {showPreview ? 'Ocultar Preview' : 'Mostrar Preview'}
                    </button>
                    
                    <button 
                        onClick={handleDownload}
                        className="action-button action-button-secondary"
                        disabled={isLoading || !template.conteudo}
                    >
                        {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        Download
                    </button>
                    
                    <button 
                        onClick={handlePrint}
                        className="action-button action-button-primary"
                        disabled={isLoading || !template.conteudo}
                    >
                        {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                        Imprimir/PDF
                    </button>
                </div>
            </fieldset>

            {/* PREVIEW DO CONTEÚDO */}
            {showPreview && (
                <fieldset className="form-section">
                    <legend className="form-section-title">
                        Preview da Anamnese - Página {currentPage} de {totalPages}
                    </legend>
                    
                    {isLoading ? (
                        <div className="panel-state-wrapper">
                            <Loader2 size={48} className="animate-spin text-blue-500" />
                            <p>Processando anamnese...</p>
                        </div>
                    ) : !template.conteudo || !Array.isArray(template.conteudo) || template.conteudo.length === 0 ? (
                        <div className="panel-state-wrapper error">
                            <AlertCircle size={48} className="text-yellow-500" />
                            <h4>Anamnese Sem Conteúdo</h4>
                            <p>Esta anamnese não possui campos configurados para visualização.</p>
                        </div>
                    ) : pageContents.length > 0 ? (
                        <div className="a4-preview-wrapper">
                            {/* CONTROLES DE PAGINAÇÃO */}
                            <div className="editor-toolbar" style={{ marginBottom: '1rem' }}>
                                <button 
                                    onClick={prevPage}
                                    disabled={currentPage === 1}
                                    className="action-button action-button-secondary"
                                >
                                    <ChevronLeft size={16} />
                                    Anterior
                                </button>
                                
                                <span className="pagination-info" style={{ 
                                    padding: '0.5rem 1rem', 
                                    fontSize: '0.875rem',
                                    color: '#6b7280'
                                }}>
                                    Página {currentPage} de {totalPages}
                                </span>
                                
                                <button 
                                    onClick={nextPage}
                                    disabled={currentPage === totalPages}
                                    className="action-button action-button-secondary"
                                >
                                    Próxima
                                    <ChevronRight size={16} />
                                </button>
                            </div>

                            {/* CONTAINER DA PÁGINA ATUAL */}
                            <div className="single-page-container">
                                <div className="a4-page-single">
                                    <iframe
                                        ref={iframeRef}
                                        srcDoc={createPageContent(currentPage)}
                                        title={`Anamnese: ${template.nome} - Página ${currentPage}`}
                                        className="preview-iframe-single"
                                        sandbox="allow-same-origin"
                                        onLoad={() => setIsLoading(false)}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="panel-state-wrapper">
                            <AlertCircle size={48} className="text-yellow-500" />
                            <p>Anamnese não disponível para visualização</p>
                        </div>
                    )}
                </fieldset>
            )}

            {/* BOTÕES DE AÇÃO NO FINAL */}
            <div className="form-actions">
                <button 
                    type="button" 
                    onClick={onClose}
                    className="action-button action-button-secondary"
                >
                    <ArrowLeft size={18} />
                    Voltar para Lista
                </button>
            </div>
        </div>
    );
};

export default AnamnesePreview;